// A tiny declarative schema language, and the reason it exists.
//
// The status document has three consumers in three languages: `installer/
// status.mjs` writes it (Node, inside the image), the CLI reads it (TypeScript),
// and the install page will read it (TypeScript in a browser, Rust later if the
// desktop shell lands). A TypeScript `interface` is not a contract across that
// set — it compiles to nothing and validates nothing, which is exactly how three
// fields (`stampSeed`, `updaterProvision`, `platformVersion`) shipped undeclared
// and a fourth (`warnings`) was still undeclared when this package was written.
//
// So the field list is DATA, declared once here, and everything else is derived
// from it: the JSON Schema artifact (committed, language-neutral), the runtime
// validator, and the drift test that reads the real writer. Deriving both halves
// from one source is the point — a validator and a schema maintained separately
// drift, and then the schema says one thing while the code enforces another.
//
// Deliberately dependency-free. Pulling ajv in would put a runtime dep in the
// CLI bundle and in the browser page to check a document with eleven fields.

/** A node in the field spec. */
export type SpecNode =
  | { kind: "literal"; value: number }
  | { kind: "number" }
  | { kind: "string" }
  | { kind: "boolean" }
  | { kind: "enum"; values: readonly string[] }
  | { kind: "array"; items: SpecNode }
  | { kind: "map"; values: SpecNode }
  | { kind: "object"; fields: Readonly<Record<string, Field>> }
  /** `inner` or an explicit JSON null. Distinct from optional, which is absence. */
  | { kind: "nullable"; inner: SpecNode };

/** One field of an object node. */
export interface Field {
  readonly node: SpecNode;
  /**
   * Required fields are the ones a consumer cannot work without. Everything the
   * writer emits is DECLARED, but most of it is optional: a document written by
   * an older installer may legitimately lack a field, and refusing it would turn
   * a readable status into an unreadable one for no gain. Undeclared fields are
   * a different matter — those are caught by the closed-object rule below.
   */
  readonly required: boolean;
  /** Why this field exists / what writes it. Rendered into the JSON Schema. */
  readonly doc?: string;
}

export const lit = (value: number): SpecNode => ({ kind: "literal", value });
export const num = (): SpecNode => ({ kind: "number" });
export const str = (): SpecNode => ({ kind: "string" });
export const bool = (): SpecNode => ({ kind: "boolean" });
export const enom = (...values: readonly string[]): SpecNode => ({ kind: "enum", values });
export const arr = (items: SpecNode): SpecNode => ({ kind: "array", items });
export const map = (values: SpecNode): SpecNode => ({ kind: "map", values });
export const obj = (fields: Record<string, Field>): SpecNode => ({ kind: "object", fields });
export const nullable = (inner: SpecNode): SpecNode => ({ kind: "nullable", inner });

export const req = (node: SpecNode, doc?: string): Field => ({ node, required: true, ...(doc ? { doc } : {}) });
export const opt = (node: SpecNode, doc?: string): Field => ({ node, required: false, ...(doc ? { doc } : {}) });

/** One reason a document failed validation, as a JSON-Pointer-ish path + message. */
export interface Violation {
  readonly path: string;
  readonly message: string;
}

/**
 * What to do about a field the contract does not declare.
 *
 * `"reject"` is for CI: the generated schema closes every object, and the drift
 * test compares the contract against the REAL writer, so an undeclared field
 * fails a build and a person fixes it. That is the guard this package exists to
 * be.
 *
 * `"ignore"` is for runtime readers, and the distinction is not a softening —
 * it is the difference between failing a developer and failing a founder.
 * Enforcing closure at runtime would make every future field addition a
 * BREAKING change: an installer that adds one optional field would become
 * unreadable to every CLI already installed, including the one an operator uses
 * to look at somebody else's in-flight install. Additive changes have to stay
 * additive on the read side, or "prefer adding an optional field" is not advice
 * anyone can take.
 *
 * Unknown *versions* are still refused outright — see `parseStatusDoc`. A
 * version bump says the semantics moved; a new field does not.
 */
export type UnknownFieldPolicy = "reject" | "ignore";

export interface ValidateOptions {
  readonly unknownFields?: UnknownFieldPolicy;
}

/**
 * Validate `value` against `node`.
 *
 * Undeclared fields are violations under the default `"reject"` policy — that
 * is what lets the cross-language test notice the writer emitting something the
 * contract never declared, the exact failure that has already happened four
 * times on this document.
 */
export function validateNode(
  node: SpecNode,
  value: unknown,
  path = "",
  opts: ValidateOptions = {},
): Violation[] {
  const at = path === "" ? "(root)" : path;
  switch (node.kind) {
    case "literal":
      return value === node.value ? [] : [{ path: at, message: `expected the literal ${String(node.value)}, got ${describe(value)}` }];
    case "number":
      return typeof value === "number" && Number.isFinite(value)
        ? []
        : [{ path: at, message: `expected a finite number, got ${describe(value)}` }];
    case "string":
      return typeof value === "string" ? [] : [{ path: at, message: `expected a string, got ${describe(value)}` }];
    case "boolean":
      return typeof value === "boolean" ? [] : [{ path: at, message: `expected a boolean, got ${describe(value)}` }];
    case "enum":
      return typeof value === "string" && node.values.includes(value)
        ? []
        : [{ path: at, message: `expected one of ${node.values.join(" | ")}, got ${describe(value)}` }];
    case "nullable":
      return value === null ? [] : validateNode(node.inner, value, path, opts);
    case "array": {
      if (!Array.isArray(value)) return [{ path: at, message: `expected an array, got ${describe(value)}` }];
      return value.flatMap((v, i) => validateNode(node.items, v, `${path}[${String(i)}]`, opts));
    }
    case "map": {
      if (!isPlainObject(value)) return [{ path: at, message: `expected an object, got ${describe(value)}` }];
      return Object.entries(value).flatMap(([k, v]) => validateNode(node.values, v, `${path}.${k}`, opts));
    }
    case "object": {
      if (!isPlainObject(value)) return [{ path: at, message: `expected an object, got ${describe(value)}` }];
      const out: Violation[] = [];
      for (const [name, field] of Object.entries(node.fields)) {
        const present = Object.prototype.hasOwnProperty.call(value, name);
        if (!present) {
          if (field.required) out.push({ path: `${path}.${name}`, message: "is required but missing" });
          continue;
        }
        out.push(...validateNode(field.node, value[name], `${path}.${name}`, opts));
      }
      if ((opts.unknownFields ?? "reject") === "reject") {
        for (const name of Object.keys(value)) {
          if (!Object.prototype.hasOwnProperty.call(node.fields, name)) {
            out.push({
              path: `${path}.${name}`,
              message:
                "is not declared by the contract. If the installer legitimately writes it, declare it here " +
                "and regenerate the schema — do not widen the object.",
            });
          }
        }
      }
      return out;
    }
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function describe(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "an array";
  return typeof v;
}

/** Render a spec node as a JSON Schema (draft 2020-12) fragment. */
export function toJsonSchema(node: SpecNode): Record<string, unknown> {
  switch (node.kind) {
    case "literal":
      return { const: node.value };
    case "number":
      return { type: "number" };
    case "string":
      return { type: "string" };
    case "boolean":
      return { type: "boolean" };
    case "enum":
      return { type: "string", enum: [...node.values] };
    case "nullable":
      return { anyOf: [toJsonSchema(node.inner), { type: "null" }] };
    case "array":
      return { type: "array", items: toJsonSchema(node.items) };
    case "map":
      return { type: "object", additionalProperties: toJsonSchema(node.values) };
    case "object": {
      const properties: Record<string, unknown> = {};
      const required: string[] = [];
      for (const [name, field] of Object.entries(node.fields)) {
        properties[name] = field.doc ? { ...toJsonSchema(field.node), description: field.doc } : toJsonSchema(field.node);
        if (field.required) required.push(name);
      }
      return {
        type: "object",
        properties,
        ...(required.length > 0 ? { required } : {}),
        // Closed on purpose — see validateNode.
        additionalProperties: false,
      };
    }
  }
}
